import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { casesService } from "../services/cases.service";
import type {
  AssignmentHistoryPage,
  AssignmentRecoveryState,
  ReassignmentContext,
  ReplacePathologistRequest,
  ReplacePathologistResponse,
} from "../types/casePathologistAssignment";
import { getCasePathologistAssignmentError } from "../types/casePathologistAssignment";
import { useHasPermission } from "./useHasPermission";
import {
  assignmentChangeInvalidationTargets,
  casePathologistAssignmentQueryKeys,
} from "../queryKeys/casePathologistAssignmentQueryKeys";
import {
  hasReplacementFollowUp,
  replacementCompletionMessage,
} from "../utils/casePathologistAssignmentPresentation";

const REASSIGN_PERMISSION = "cases.pathologist.reassign";
const idleRecoveryState: AssignmentRecoveryState = {
  status: "idle",
  canRetry: false,
};

interface UseCasePathologistAssignmentOptions {
  enabled: boolean;
}

interface AmbiguousRequest {
  fingerprint: string;
  request: ReplacePathologistRequest;
}

const getRequestFingerprint = (request: ReplacePathologistRequest): string =>
  JSON.stringify({
    expectedCurrentPathologistId: request.expectedCurrentPathologistId,
    expectedAssignmentRevision: request.expectedAssignmentRevision,
    replacementPathologistId: request.replacementPathologistId,
    reason: request.reason.trim(),
    acknowledgeActiveReview: request.acknowledgeActiveReview,
  });

const hasRecordedReplacement = (
  context: ReassignmentContext,
  history: AssignmentHistoryPage,
  request: ReplacePathologistRequest
): boolean =>
  context.currentAssignment.pathologist.id ===
    request.replacementPathologistId ||
  history.data.some(
    (event) =>
      event.fromRevision === request.expectedAssignmentRevision &&
      event.fromPathologist.id === request.expectedCurrentPathologistId &&
      event.toPathologist.id === request.replacementPathologistId
  );

const isOriginalAssignmentCurrent = (
  context: ReassignmentContext,
  request: ReplacePathologistRequest
): boolean =>
  context.currentAssignment.pathologist.id ===
    request.expectedCurrentPathologistId &&
  context.currentAssignment.revision === request.expectedAssignmentRevision;

export function useCasePathologistAssignment(
  caseId: string,
  { enabled }: UseCasePathologistAssignmentOptions
) {
  const queryClient = useQueryClient();
  const canReplacePathologist = useHasPermission(REASSIGN_PERMISSION);
  const activeRequestRef = useRef<Promise<ReplacePathologistResponse> | null>(
    null
  );
  const ambiguousRequestRef = useRef<AmbiguousRequest | null>(null);
  const [recoveryState, setRecoveryState] =
    useState<AssignmentRecoveryState>(idleRecoveryState);

  const contextQuery = useQuery({
    queryKey: casePathologistAssignmentQueryKeys.context(caseId),
    queryFn: () => casesService.getPathologistReassignmentContext(caseId),
    enabled: enabled && canReplacePathologist && Boolean(caseId),
  });

  const invalidateAssignmentQueries = useCallback(async () => {
    await Promise.all(
      assignmentChangeInvalidationTargets(caseId).map((target) =>
        queryClient.invalidateQueries(target)
      )
    );
  }, [caseId, queryClient]);

  const reconcileAmbiguousRequest = useCallback(
    async (
      request: ReplacePathologistRequest
    ): Promise<ReplacePathologistResponse> => {
      setRecoveryState({
        status: "reconciling",
        canRetry: false,
        message: "Checking the recorded assignment before allowing a retry.",
      });

      let context: ReassignmentContext;
      let history: AssignmentHistoryPage;
      try {
        [context, history] = await Promise.all([
          casesService.getPathologistReassignmentContext(caseId),
          casesService.getCasePathologistAssignmentHistory(caseId),
        ]);
      } catch {
        setRecoveryState({
          status: "unresolved",
          canRetry: false,
          message:
            "The result could not be confirmed. Refresh the case before trying again.",
        });
        throw new Error(
          "The replacement result could not be confirmed. Do not retry yet."
        );
      }

      if (hasRecordedReplacement(context, history, request)) {
        try {
          return await casesService.replaceCasePathologist(caseId, request);
        } catch {
          setRecoveryState({
            status: "unresolved",
            canRetry: false,
            message:
              "The replacement was recorded, but its result could not be loaded. Refresh the case before continuing.",
          });
          throw new Error(
            "The recorded replacement result could not be loaded. Do not retry yet."
          );
        }
      }

      if (isOriginalAssignmentCurrent(context, request)) {
        setRecoveryState({
          status: "retry_available",
          canRetry: true,
          message:
            "No assignment change was recorded. It is safe to retry this replacement.",
        });
        throw new Error(
          "No assignment change was recorded. It is safe to retry."
        );
      }

      ambiguousRequestRef.current = null;
      setRecoveryState({
        status: "unresolved",
        canRetry: false,
        message:
          "The assignment changed while the result was being checked. Review the current owner.",
      });
      throw new Error(
        "The current assignment changed. Review the current owner before continuing."
      );
    },
    [caseId]
  );

  const executeReplacement = useCallback(
    async (
      requestedCommand: ReplacePathologistRequest
    ): Promise<ReplacePathologistResponse> => {
      const fingerprint = getRequestFingerprint(requestedCommand);
      const retainedRequest = ambiguousRequestRef.current;
      const request =
        retainedRequest?.fingerprint === fingerprint
          ? retainedRequest.request
          : requestedCommand;

      if (retainedRequest?.fingerprint !== fingerprint) {
        ambiguousRequestRef.current = null;
        setRecoveryState(idleRecoveryState);
      }

      try {
        const response = await casesService.replaceCasePathologist(
          caseId,
          request
        );
        ambiguousRequestRef.current = null;
        setRecoveryState(idleRecoveryState);
        return response;
      } catch (error) {
        const assignmentError = getCasePathologistAssignmentError(error);
        if (assignmentError) {
          ambiguousRequestRef.current = null;
          setRecoveryState(idleRecoveryState);
          if (assignmentError.code === "PATHOLOGIST_ASSIGNMENT_STALE") {
            await contextQuery.refetch();
          }
          throw error;
        }

        ambiguousRequestRef.current = { fingerprint, request };
        return reconcileAmbiguousRequest(request);
      }
    },
    [caseId, contextQuery, reconcileAmbiguousRequest]
  );

  const mutation = useMutation({
    mutationFn: (request: Promise<ReplacePathologistResponse>) => request,
    onSuccess: (response) => {
      void invalidateAssignmentQueries();
      notifications.show({
        title: "Pathologist replaced",
        message: replacementCompletionMessage(response),
        color: "green",
        autoClose: hasReplacementFollowUp(response) ? false : 4000,
      });
    },
  });

  const submitReplacement = useCallback(
    (request: ReplacePathologistRequest) => {
      if (activeRequestRef.current) {
        return activeRequestRef.current;
      }

      const serviceRequest = executeReplacement(request);
      const activeRequest = mutation.mutateAsync(serviceRequest);
      activeRequestRef.current = activeRequest;
      const clearActiveRequest = () => {
        if (activeRequestRef.current === activeRequest) {
          activeRequestRef.current = null;
        }
      };
      void activeRequest.then(clearActiveRequest, clearActiveRequest);
      return activeRequest;
    },
    [executeReplacement, mutation]
  );

  const submitReplacementWithoutResult = useCallback(
    (request: ReplacePathologistRequest) => {
      void submitReplacement(request);
    },
    [submitReplacement]
  );

  const replaceMutation = useMemo(
    () => ({
      ...mutation,
      mutate: submitReplacementWithoutResult,
      mutateAsync: submitReplacement,
    }),
    [mutation, submitReplacement, submitReplacementWithoutResult]
  );

  return {
    canReplacePathologist,
    contextQuery,
    replaceMutation,
    recoveryState,
  };
}
